import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import classNames from "classnames";

import StyleButton from "editor/components/StyleButton";
import withTooltip from "components/library/Tooltip/withTooltip";

import { toggleInlineStyle, toggleBlockType } from "editor/actions/editor";
import {
  insertKeepReading,
  toggleQuoteWithAttribution,
} from "editor/actions/blocks";
import { toggleModal } from "editor/actions/ui";
import { saveForm } from "editor/actions/status";
import getActiveEditorState from "editor/utils/getActiveEditorState";
import isActiveInlineStyle from "editor/utils/isActiveInlineStyle";
import isActiveBlockType from "editor/utils/isActiveBlockType";
import getActiveBlockType from "editor/utils/getActiveBlockType";
import hasBlockWithType from "editor/utils/hasBlockWithType";
import isEditorActive from "editor/utils/isEditorActive";
import {
  EDITOR_MODALS,
  BLOCK_TYPE,
  ENTITY_TYPE,
  INLINE_TOOLS_ONLY_BLOCKS,
} from "config/constants/editor";
import styles from "./Toolbar.css";

const gtmClassPrefix = "gtm-format-button-";
const inlineStyles = [
  {
    style: "BOLD",
    label: "Bold",
    icon: "bold",
    className: `${gtmClassPrefix}bold`,
  },
  {
    style: "ITALIC",
    label: "Italic",
    icon: "italic",
    className: `${gtmClassPrefix}italic`,
  },
];

const blockStyles = [
  {
    style: BLOCK_TYPE.UNORDERED_LIST_ITEM,
    label: "Unordered list",
    icon: "list",
    className: `${gtmClassPrefix}unordered-list`,
  },
  {
    style: BLOCK_TYPE.ORDERED_LIST_ITEM,
    label: "Ordered list",
    icon: "list-ordered",
    className: `${gtmClassPrefix}ordered-list`,
  },
];

const subhead = {
  style: BLOCK_TYPE.SUBHEAD,
  label: "H2",
  icon: "h2",
  className: `${gtmClassPrefix}subhead`,
};
const link = {
  style: ENTITY_TYPE.LINK,
  label: "Link",
  icon: "link",
  className: `${gtmClassPrefix}link`,
};
const embed = {
  style: BLOCK_TYPE.EMBED,
  label: "Embed",
  icon: "code",
  className: `${gtmClassPrefix}embed`,
};
const plainQuote = {
  style: BLOCK_TYPE.PLAIN_QUOTE,
  label: "Quote",
  icon: "quote",
  className: `${gtmClassPrefix}plain-quote`,
};
const image = {
  style: BLOCK_TYPE.IMAGE,
  label: "Image",
  icon: "image",
  className: `${gtmClassPrefix}image`,
};
const keepReading = {
  style: BLOCK_TYPE.KEEP_READING,
  label: "Go deeper",
  icon: "go-deeper",
  className: `${gtmClassPrefix}keep-reading`,
};

class Toolbar extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      shouldShowMore: false,
    };

    this.toggleLinkModal = this._toggleLinkModal.bind(this);
    this.toggleEmbedModal = this._toggleEmbedModal.bind(this);
    this.activateImageEditor = this._activateImageEditor.bind(this);
  }

  _toggleLinkModal() {
    this.props.toggleModal(EDITOR_MODALS.LINK_EDITOR);
  }

  _toggleEmbedModal() {
    this.props.toggleModal(EDITOR_MODALS.EMBED_EDITOR);
  }

  _activateImageEditor() {
    return this.props.saveForm().then(() => {
      this.props.history.replace(this.props.getBaseUrl()); // add ID if article hasn't been saved yet
      this.props.history.push(this.props.getBaseUrl() + "image");
    });
  }

  render() {
    const editorActive = isEditorActive(this.props.editorState);
    const isInline =
      this.props.isInline ||
      INLINE_TOOLS_ONLY_BLOCKS.includes(
        getActiveBlockType(this.props.editorState)
      );
    const StyleButtonTooltip = withTooltip(StyleButton);
    const keepReadingToolTip =
      this.props.editorType === "content"
        ? keepReading.label
        : `${keepReading.label} (App Only)`;

    const embedGroup = (
      <div className={styles.group}>
        <StyleButtonTooltip
          key={link.style}
          onToggle={this.toggleLinkModal}
          isActive={false}
          isDisabled={!editorActive}
          tooltip={editorActive && link.label}
          {...link}
        />
        <StyleButtonTooltip
          key={image.style}
          onToggle={this.activateImageEditor}
          isActive={false}
          isDisabled={!editorActive || isInline}
          tooltip={editorActive && image.label}
          {...image}
        />
        <StyleButtonTooltip
          key={embed.style}
          onToggle={this.toggleEmbedModal}
          isActive={false}
          isDisabled={!editorActive || isInline}
          tooltip={editorActive && embed.label}
          {...embed}
        />
        <StyleButtonTooltip
          key={keepReading.style}
          onToggle={this.props.insertKeepReading}
          isActive={false}
          isDisabled={
            !editorActive ||
            hasBlockWithType(this.props.editorState, BLOCK_TYPE.KEEP_READING) ||
            isInline
          }
          tooltip={editorActive && keepReadingToolTip}
          {...keepReading}
        />
      </div>
    );

    return (
      <>
        <div className={styles.main}>
          <div className={styles.group}>
            {inlineStyles.map(style => {
              return (
                <StyleButtonTooltip
                  key={style.style}
                  onToggle={this.props.toggleInlineStyle}
                  isActive={isActiveInlineStyle(
                    this.props.editorState,
                    style.style
                  )}
                  isDisabled={!editorActive}
                  tooltip={editorActive && style.label}
                  {...style}
                />
              );
            })}
            <StyleButtonTooltip
              key={plainQuote.style}
              onToggle={this.props.toggleQuoteWithAttribution}
              isActive={
                isActiveBlockType(
                  this.props.editorState,
                  BLOCK_TYPE.PLAIN_QUOTE
                ) ||
                isActiveBlockType(
                  this.props.editorState,
                  BLOCK_TYPE.QUOTE_ATTRIBUTION
                )
              }
              isDisabled={!editorActive || isInline}
              tooltip={editorActive && plainQuote.label}
              {...plainQuote}
            />
            <StyleButtonTooltip
              key={subhead.style}
              onToggle={this.props.toggleBlockType}
              isActive={isActiveBlockType(
                this.props.editorState,
                subhead.style
              )}
              isDisabled={!editorActive || isInline}
              tooltip={editorActive && subhead.label}
              {...subhead}
            />
          </div>

          <div className={styles.group}>
            {blockStyles.map(style => {
              return (
                <StyleButtonTooltip
                  key={style.style}
                  onToggle={this.props.toggleBlockType}
                  isActive={isActiveBlockType(
                    this.props.editorState,
                    style.style
                  )}
                  isDisabled={!editorActive || isInline}
                  tooltip={editorActive && style.label}
                  {...style}
                />
              );
            })}
          </div>

          <div className={styles.mobile}>
            <StyleButton
              icon="more-horizontal"
              isActive={this.state.shouldShowMore}
              isDisabled={isInline}
              label="More options"
              onToggle={() =>
                this.setState({ shouldShowMore: !this.state.shouldShowMore })
              }
              style=""
            />
          </div>
          <div className={styles.desktop}>{embedGroup}</div>
        </div>
        {this.state.shouldShowMore && (
          <div className={classNames(styles.options, styles.spacing)}>
            {embedGroup}
          </div>
        )}
      </>
    );
  }
}

const mapStateToProps = state => {
  return {
    editorState: getActiveEditorState(state),
    isInline: state.editor.status.activeEditor.isInline,
    editorType: state.editor.status.editorType.name,
  };
};

const mapDispatchToProps = {
  insertKeepReading,
  toggleInlineStyle,
  toggleBlockType,
  toggleModal,
  toggleQuoteWithAttribution,
  saveForm,
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Toolbar)
);

Toolbar.propTypes = {
  toggleInlineStyle: PropTypes.func.isRequired,
  editorState: PropTypes.object.isRequired,
  toggleBlockType: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  toggleQuoteWithAttribution: PropTypes.func.isRequired,
  editorType: PropTypes.string,
  activeToolbarGroup: PropTypes.string,
  getBaseUrl: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
};
